import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { PLAN_TIERS } from '../../utils/constants';
import './TeamManagement.css';

export default function TeamManagement() {
  const { user } = useAuth();
  const [practitioner, setPractitioner] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Invite form
  const [showInvite, setShowInvite] = useState(false);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (user) loadTeam();
  }, [user]);

  async function loadTeam() {
    setLoading(true);

    const { data: prac } = await supabase
      .from('practitioners')
      .select('*')
      .eq('auth_user_id', user.id)
      .single();

    setPractitioner(prac);

    if (prac) {
      const { data: teamData } = await supabase
        .from('team_members')
        .select('*')
        .eq('practitioner_id', prac.id)
        .order('invited_at', { ascending: false });

      setMembers(teamData || []);
    }
    setLoading(false);
  }

  async function handleInvite(e) {
    e.preventDefault();
    setError('');
    setSuccess('');
    setInviting(true);

    // Check plan limits
    const tier = PLAN_TIERS[practitioner.plan_tier || 'starter'];
    if (tier.maxTeamMembers === 0) {
      setError('Your current plan doesn\'t include team members. Upgrade to Practice to invite assistants.');
      setInviting(false);
      return;
    }
    if (members.length >= tier.maxTeamMembers) {
      setError(`Your plan allows up to ${tier.maxTeamMembers} team member${tier.maxTeamMembers !== 1 ? 's' : ''}. Upgrade for more.`);
      setInviting(false);
      return;
    }

    // Check for duplicate
    if (members.some((m) => m.email.toLowerCase() === inviteEmail.toLowerCase())) {
      setError('This person is already on your team.');
      setInviting(false);
      return;
    }

    const { error: insertError } = await supabase
      .from('team_members')
      .insert({
        practitioner_id: practitioner.id,
        name: inviteName,
        email: inviteEmail,
        role: 'assistant',
      });

    if (insertError) {
      setError(insertError.message);
    } else {
      setSuccess(`Invited ${inviteName} as an assistant.`);
      setInviteName('');
      setInviteEmail('');
      setShowInvite(false);
      loadTeam();
    }
    setInviting(false);
  }

  async function removeMember(memberId, memberName) {
    if (!window.confirm(`Remove ${memberName} from your team? They will lose access to all client data.`)) return;

    await supabase.from('team_members').delete().eq('id', memberId);
    loadTeam();
  }

  if (loading) {
    return (
      <div className="screen container">
        <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--color-text-muted)' }}>
          Loading team...
        </div>
      </div>
    );
  }

  const tier = PLAN_TIERS[practitioner?.plan_tier || 'starter'];
  const canInvite = tier.maxTeamMembers > 0 && members.length < tier.maxTeamMembers;

  return (
    <div className="screen container team-screen">
      <h1 className="team-title">Team Management</h1>
      <p className="team-desc">
        Assistants can view all client data and edit protocols, but cannot manage billing or invite others.
      </p>

      {error && <div className="form-error" style={{ marginBottom: '16px' }}>{error}</div>}
      {success && <div className="team-success">{success}</div>}

      {/* Team members list */}
      <div className="team-section">
        <div className="team-section-header">
          <h3 className="team-section-title">
            Team Members
            <span className="team-count">{members.length}{tier.maxTeamMembers > 0 ? ` / ${tier.maxTeamMembers}` : ''}</span>
          </h3>
          {canInvite && (
            <button className="btn btn-primary" style={{ padding: '8px 16px', fontSize: '0.85rem' }} onClick={() => setShowInvite(true)}>
              + Invite
            </button>
          )}
        </div>

        {/* Practitioner (always shown) */}
        <div className="team-member-card">
          <div className="team-member-info">
            <div className="team-member-avatar tm-owner">
              {practitioner?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
            </div>
            <div>
              <p className="team-member-name">{practitioner?.name}</p>
              <p className="team-member-email">{practitioner?.email}</p>
            </div>
          </div>
          <span className="team-role-badge role-practitioner">Practitioner</span>
        </div>

        {/* Assistants */}
        {members.map((member) => (
          <div key={member.id} className="team-member-card">
            <div className="team-member-info">
              <div className="team-member-avatar tm-assistant">
                {member.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
              </div>
              <div>
                <p className="team-member-name">{member.name}</p>
                <p className="team-member-email">{member.email}</p>
                {!member.accepted_at && (
                  <p className="team-member-pending">Invite pending</p>
                )}
              </div>
            </div>
            <div className="team-member-actions">
              <span className="team-role-badge role-assistant">Assistant</span>
              <button
                className="team-remove-btn"
                onClick={() => removeMember(member.id, member.name)}
                title="Remove team member"
              >
                ✕
              </button>
            </div>
          </div>
        ))}

        {members.length === 0 && (
          <div className="team-empty">
            <p>No team members yet.</p>
            {tier.maxTeamMembers === 0 ? (
              <p className="team-empty-sub">Upgrade to the Practice plan to invite up to 3 assistants.</p>
            ) : (
              <p className="team-empty-sub">Invite an assistant to help manage your clients.</p>
            )}
          </div>
        )}
      </div>

      {/* Invite form overlay */}
      {showInvite && (
        <div className="team-overlay">
          <div className="team-invite-panel">
            <div className="team-invite-header">
              <h3>Invite Assistant</h3>
              <button onClick={() => { setShowInvite(false); setError(''); }} className="template-close">✕</button>
            </div>
            <p className="team-invite-desc">
              They'll receive access to view and edit all client protocols.
            </p>
            <form className="team-invite-form" onSubmit={handleInvite}>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Assistant's name"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  className="form-input"
                  placeholder="assistant@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  required
                />
              </div>
              <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={inviting}>
                {inviting ? 'Inviting...' : 'Send Invite'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
